import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  getAdminEvent,
  createEvent,
  updateEvent,
  createSection,
  updateSection,
  deleteSection,
  createQuestion,
  updateQuestion,
  deleteQuestion,
} from '../../services/adminApi';
import type { Event, Question, Section } from '../../types';
import type { CreateQuestionData } from '../../services/adminApi';
import './Admin.css';

export default function EventEditor() {
  const { id } = useParams<{ id: string }>();
  const isNew = id === 'new';
  const navigate = useNavigate();

  const [event, setEvent] = useState<Partial<Event>>({
    name: '',
    slug: '',
    description: '',
    event_date: new Date().toISOString().split('T')[0],
    duration_minutes: null,
    is_active: 1,
  });
  const [sections, setSections] = useState<Section[]>([]);
  const [questions, setQuestions] = useState<Question[]>([]);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [editingSection, setEditingSection] = useState<number | null>(null);
  const [editingQuestion, setEditingQuestion] = useState<number | null>(null);
  const [sectionEditName, setSectionEditName] = useState('');
  const [questionEditData, setQuestionEditData] = useState<Partial<Question>>({});

  useEffect(() => {
    if (!isNew) {
      loadEvent();
    }
  }, [id, isNew]);

  const loadEvent = async () => {
    if (!id || isNew) return;
    try {
      setLoading(true);
      const data = await getAdminEvent(parseInt(id));
      setEvent(data.event);
      setSections(data.sections);
      setQuestions(data.questions);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load event');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveEvent = async () => {
    if (!event.name || !event.slug || !event.event_date) {
      setError('Name, slug, and date are required');
      return;
    }

    try {
      setSaving(true);
      setError('');
      let savedEvent: Event;
      if (isNew) {
        savedEvent = await createEvent({
          name: event.name!,
          slug: event.slug!,
          description: event.description || undefined,
          event_date: event.event_date!,
          duration_minutes: event.duration_minutes || undefined,
          is_active: event.is_active || 1,
        });
        navigate(`/admin/events/${savedEvent.id}`);
      } else {
        savedEvent = await updateEvent(parseInt(id!), {
          name: event.name,
          slug: event.slug,
          description: event.description ?? undefined,
          event_date: event.event_date,
          duration_minutes: event.duration_minutes ?? undefined,
          is_active: event.is_active,
        });
        setEvent(savedEvent);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save event');
    } finally {
      setSaving(false);
    }
  };

  const handleAddSection = async () => {
    if (!id || isNew) {
      alert('Please save the event first');
      return;
    }
    const name = prompt('Section name:');
    if (!name) return;
    try {
      const section = await createSection({
        event_id: parseInt(id),
        name,
        sort_order: sections.length,
      });
      setSections([...sections, section]);
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to create section');
    }
  };

  const handleEditSection = (section: Section) => {
    setEditingSection(section.id);
    setSectionEditName(section.name);
  };

  const handleSaveSection = async (sectionId: number) => {
    if (!sectionEditName.trim()) {
      alert('Section name is required');
      return;
    }
    try {
      const updated = await updateSection(sectionId, { name: sectionEditName.trim() });
      setSections(sections.map((s) => (s.id === sectionId ? updated : s)));
      setEditingSection(null);
      setSectionEditName('');
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to update section');
    }
  };

  const handleCancelEditSection = () => {
    setEditingSection(null);
    setSectionEditName('');
  };

  const handleDeleteSection = async (sectionId: number) => {
    if (!window.confirm('Delete this section? Questions in this section will be moved to "No section".')) {
      return;
    }
    try {
      await deleteSection(sectionId);
      setSections(sections.filter((s) => s.id !== sectionId));
      setQuestions(questions.map((q) => (q.section_id === sectionId ? { ...q, section_id: null } : q)));
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to delete section');
    }
  };

  const handleAddQuestion = async () => {
    if (!id || isNew) {
      alert('Please save the event first');
      return;
    }
    const questionType = prompt('Question type (STAR_RATING, CHOOSE_MULTIPLE, TEXT):') as
      | 'STAR_RATING'
      | 'CHOOSE_MULTIPLE'
      | 'TEXT';
    if (!questionType || !['STAR_RATING', 'CHOOSE_MULTIPLE', 'TEXT'].includes(questionType)) {
      return;
    }
    const questionText = prompt('Question text:');
    if (!questionText) return;

    const sectionIdStr = prompt('Section ID (leave empty for no section):');
    const sectionId = sectionIdStr ? parseInt(sectionIdStr) : undefined;

    const questionData: CreateQuestionData = {
      event_id: parseInt(id),
      section_id: sectionId,
      question_type: questionType,
      question_text: questionText,
      sort_order: questions.length,
    };

    if (questionType === 'STAR_RATING') {
      questionData.min_value = 1;
      questionData.max_value = 5;
      questionData.step_value = 0.25;
    } else if (questionType === 'CHOOSE_MULTIPLE') {
      const optionsStr = prompt('Options (comma-separated):');
      if (optionsStr) {
        questionData.options = optionsStr.split(',').map((opt, idx) => ({
          value: opt.trim().toLowerCase().replace(/\s+/g, '-'),
          label: opt.trim(),
          sort_order: idx,
        }));
      }
    }

    try {
      const question = await createQuestion(questionData);
      setQuestions([...questions, question]);
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to create question');
    }
  };

  const handleEditQuestion = (question: Question) => {
    setEditingQuestion(question.id);
    setQuestionEditData({
      question_text: question.question_text,
      question_type: question.question_type,
      section_id: question.section_id,
      min_value: question.min_value,
      max_value: question.max_value,
      step_value: question.step_value,
      options: question.options || [],
    });
  };

  const handleSaveQuestion = async (questionId: number) => {
    if (!questionEditData.question_text?.trim()) {
      alert('Question text is required');
      return;
    }
    try {
      const updates: any = {
        question_text: questionEditData.question_text,
        section_id: questionEditData.section_id ?? null,
      };
      if (questionEditData.question_type) {
        updates.question_type = questionEditData.question_type;
      }
      if (questionEditData.question_type === 'STAR_RATING') {
        updates.min_value = questionEditData.min_value ?? 1;
        updates.max_value = questionEditData.max_value ?? 5;
        updates.step_value = questionEditData.step_value ?? 0.25;
      }
      if (questionEditData.question_type === 'CHOOSE_MULTIPLE' && questionEditData.options) {
        updates.options = questionEditData.options.map((o) => ({
          value: o.option_value,
          label: o.option_label,
          sort_order: o.sort_order,
        }));
      }
      const updated = await updateQuestion(questionId, updates);
      setQuestions(questions.map((q) => (q.id === questionId ? updated : q)));
      setEditingQuestion(null);
      setQuestionEditData({});
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to update question');
    }
  };

  const handleCancelEditQuestion = () => {
    setEditingQuestion(null);
    setQuestionEditData({});
  };

  const handleDeleteQuestion = async (questionId: number) => {
    if (!window.confirm('Delete this question?')) return;
    try {
      await deleteQuestion(questionId);
      setQuestions(questions.filter((q) => q.id !== questionId));
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to delete question');
    }
  };

  if (loading) {
    return (
      <div className="admin-page">
        <div className="admin-layout">
          <div className="loading">Loading event...</div>
        </div>
      </div>
    );
  }

  const questionsBySection = questions.reduce((acc, q) => {
    const sid = q.section_id || 0;
    if (!acc[sid]) acc[sid] = [];
    acc[sid].push(q);
    return acc;
  }, {} as Record<number, Question[]>);

  return (
    <div className="admin-page">
      <div className="admin-layout">
        <div className="admin-header">
          <h1 className="admin-header__title">{isNew ? 'New Event' : `Edit: ${event.name}`}</h1>
          <div className="admin-header__actions">
            <button onClick={() => navigate('/admin')} className="btn btn--ghost">
              Back
            </button>
            <button onClick={handleSaveEvent} className="btn btn--primary" disabled={saving}>
              {saving ? 'Saving...' : 'Save Event'}
            </button>
          </div>
        </div>

        {error && <div className="admin-form__error">{error}</div>}

        <div className="admin-section">
          <h2 className="admin-section__title">Event Details</h2>
          <div className="admin-form-layout">
            <div className="admin-form__group">
              <label className="admin-form__label">Name *</label>
              <input
                type="text"
                value={event.name || ''}
                onChange={(e) => setEvent({ ...event, name: e.target.value })}
                className="admin-form__input"
                placeholder="Event name"
              />
            </div>
            <div className="admin-form__group">
              <label className="admin-form__label">Slug *</label>
              <input
                type="text"
                value={event.slug || ''}
                onChange={(e) => setEvent({ ...event, slug: e.target.value })}
                className="admin-form__input"
                placeholder="url-safe-slug"
              />
            </div>
            <div className="admin-form__group">
              <label className="admin-form__label">Date *</label>
              <input
                type="date"
                value={event.event_date || ''}
                onChange={(e) => setEvent({ ...event, event_date: e.target.value })}
                className="admin-form__input"
              />
            </div>
            <div className="admin-form__group">
              <label className="admin-form__label">Duration (minutes)</label>
              <input
                type="number"
                value={event.duration_minutes || ''}
                onChange={(e) =>
                  setEvent({ ...event, duration_minutes: e.target.value ? parseInt(e.target.value) : null })
                }
                className="admin-form__input"
                placeholder="210"
              />
            </div>
            <div className="admin-form__group admin-form-layout--full">
              <label className="admin-form__label">Description</label>
              <textarea
                value={event.description || ''}
                onChange={(e) => setEvent({ ...event, description: e.target.value })}
                className="admin-form__textarea"
                placeholder="Event description"
                rows={3}
              />
            </div>
            <div className="admin-form__group">
              <label className="admin-form__label">Status</label>
              <select
                value={event.is_active || 1}
                onChange={(e) => setEvent({ ...event, is_active: parseInt(e.target.value) })}
                className="admin-form__select"
              >
                <option value={1}>Active</option>
                <option value={0}>Inactive</option>
              </select>
            </div>
          </div>
        </div>

        {!isNew && (
          <>
            <div className="admin-section">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h2 className="admin-section__title">Sections</h2>
                <button onClick={handleAddSection} className="btn btn--secondary btn--small">
                  Add Section
                </button>
              </div>
              {sections.length === 0 ? (
                <p style={{ opacity: 0.8 }}>No sections yet.</p>
              ) : (
                <div>
                  {sections.map((section) => (
                    <div key={section.id} className="question-editor">
                      {editingSection === section.id ? (
                        <div>
                          <input
                            type="text"
                            value={sectionEditName}
                            onChange={(e) => setSectionEditName(e.target.value)}
                            className="admin-form__input"
                            style={{ marginBottom: '0.75rem' }}
                            autoFocus
                          />
                          <div className="admin-actions">
                            <button
                              onClick={() => handleSaveSection(section.id)}
                              className="btn btn--primary btn--small"
                            >
                              Save
                            </button>
                            <button
                              onClick={handleCancelEditSection}
                              className="btn btn--secondary btn--small"
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="question-editor__header">
                          <span className="question-editor__title">{section.name}</span>
                          <div className="admin-actions">
                            <button
                              onClick={() => handleEditSection(section)}
                              className="btn btn--secondary btn--small"
                            >
                              Edit
                            </button>
                            <button
                              onClick={() => handleDeleteSection(section.id)}
                              className="btn btn--danger btn--small"
                            >
                              Delete
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="admin-section">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h2 className="admin-section__title">Questions</h2>
                <button onClick={handleAddQuestion} className="btn btn--secondary btn--small">
                  Add Question
                </button>
              </div>
              {questions.length === 0 ? (
                <p style={{ opacity: 0.8 }}>No questions yet.</p>
              ) : (
                <div>
                  {sections.map((section) => {
                    const sectionQuestions = questionsBySection[section.id] || [];
                    if (sectionQuestions.length === 0) return null;
                    return (
                      <div key={section.id} className="question-editor">
                        <h3 className="question-editor__title">{section.name}</h3>
                        {sectionQuestions.map((q) => (
                          <div key={q.id} style={{ marginBottom: '1rem', paddingLeft: '1rem' }}>
                            {editingQuestion === q.id ? (
                              <div>
                                <div className="admin-form__group">
                                  <label className="admin-form__label">Question Text</label>
                                  <textarea
                                    value={questionEditData.question_text || ''}
                                    onChange={(e) =>
                                      setQuestionEditData({ ...questionEditData, question_text: e.target.value })
                                    }
                                    className="admin-form__textarea"
                                    rows={2}
                                  />
                                </div>
                                <div className="admin-form__group">
                                  <label className="admin-form__label">Section</label>
                                  <select
                                    value={questionEditData.section_id || ''}
                                    onChange={(e) =>
                                      setQuestionEditData({
                                        ...questionEditData,
                                        section_id: e.target.value ? parseInt(e.target.value) : null,
                                      })
                                    }
                                    className="admin-form__select"
                                  >
                                    <option value="">No Section</option>
                                    {sections.map((s) => (
                                      <option key={s.id} value={s.id}>
                                        {s.name}
                                      </option>
                                    ))}
                                  </select>
                                </div>
                                {questionEditData.question_type === 'STAR_RATING' && (
                                  <div className="admin-form-layout">
                                    <div className="admin-form__group">
                                      <label className="admin-form__label">Min Value</label>
                                      <input
                                        type="number"
                                        step="0.01"
                                        value={questionEditData.min_value || 1}
                                        onChange={(e) =>
                                          setQuestionEditData({
                                            ...questionEditData,
                                            min_value: parseFloat(e.target.value),
                                          })
                                        }
                                        className="admin-form__input"
                                      />
                                    </div>
                                    <div className="admin-form__group">
                                      <label className="admin-form__label">Max Value</label>
                                      <input
                                        type="number"
                                        step="0.01"
                                        value={questionEditData.max_value || 5}
                                        onChange={(e) =>
                                          setQuestionEditData({
                                            ...questionEditData,
                                            max_value: parseFloat(e.target.value),
                                          })
                                        }
                                        className="admin-form__input"
                                      />
                                    </div>
                                    <div className="admin-form__group">
                                      <label className="admin-form__label">Step Value</label>
                                      <input
                                        type="number"
                                        step="0.01"
                                        value={questionEditData.step_value || 0.25}
                                        onChange={(e) =>
                                          setQuestionEditData({
                                            ...questionEditData,
                                            step_value: parseFloat(e.target.value),
                                          })
                                        }
                                        className="admin-form__input"
                                      />
                                    </div>
                                  </div>
                                )}
                                {questionEditData.question_type === 'CHOOSE_MULTIPLE' && (
                                  <div className="admin-form__group">
                                    <label className="admin-form__label">Options (one per line)</label>
                                    <textarea
                                      value={
                                        questionEditData.options
                                          ?.map((opt) => opt.option_label)
                                          .join('\n') || ''
                                      }
                                      onChange={(e) => {
                                        const labels = e.target.value.split('\n').filter((l) => l.trim());
                                        setQuestionEditData({
                                          ...questionEditData,
                                          options: labels.map((label, idx) => {
                                            const trimmed = label.trim();
                                            return {
                                              option_value: trimmed.toLowerCase().replace(/\s+/g, '-'),
                                              option_label: trimmed,
                                              sort_order: idx,
                                            };
                                          }),
                                        });
                                      }}
                                      className="admin-form__textarea"
                                      rows={4}
                                      placeholder="Option 1&#10;Option 2&#10;Option 3"
                                    />
                                  </div>
                                )}
                                <div className="admin-actions" style={{ marginTop: '0.75rem' }}>
                                  <button
                                    onClick={() => handleSaveQuestion(q.id)}
                                    className="btn btn--primary btn--small"
                                  >
                                    Save
                                  </button>
                                  <button
                                    onClick={handleCancelEditQuestion}
                                    className="btn btn--secondary btn--small"
                                  >
                                    Cancel
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                                <div style={{ flex: 1 }}>
                                  <div style={{ fontWeight: 600, marginBottom: '0.25rem' }}>{q.question_text}</div>
                                  <div style={{ fontSize: '0.875rem', opacity: 0.7 }}>
                                    {q.question_type}
                                    {q.question_type === 'STAR_RATING' &&
                                      ` (${q.min_value}-${q.max_value}, step: ${q.step_value})`}
                                    {q.question_type === 'CHOOSE_MULTIPLE' && q.options && (
                                      <span> - {q.options.length} options</span>
                                    )}
                                  </div>
                                </div>
                                <div className="admin-actions">
                                  <button
                                    onClick={() => handleEditQuestion(q)}
                                    className="btn btn--secondary btn--small"
                                  >
                                    Edit
                                  </button>
                                  <button
                                    onClick={() => handleDeleteQuestion(q.id)}
                                    className="btn btn--danger btn--small"
                                  >
                                    Delete
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    );
                  })}
                  {questionsBySection[0] && questionsBySection[0].length > 0 && (
                    <div className="question-editor">
                      <h3 className="question-editor__title">No Section</h3>
                      {questionsBySection[0].map((q) => (
                        <div key={q.id} style={{ marginBottom: '1rem', paddingLeft: '1rem' }}>
                          {editingQuestion === q.id ? (
                            <div>
                              <div className="admin-form__group">
                                <label className="admin-form__label">Question Text</label>
                                <textarea
                                  value={questionEditData.question_text || ''}
                                  onChange={(e) =>
                                    setQuestionEditData({ ...questionEditData, question_text: e.target.value })
                                  }
                                  className="admin-form__textarea"
                                  rows={2}
                                />
                              </div>
                              <div className="admin-form__group">
                                <label className="admin-form__label">Section</label>
                                <select
                                  value={questionEditData.section_id || ''}
                                  onChange={(e) =>
                                    setQuestionEditData({
                                      ...questionEditData,
                                      section_id: e.target.value ? parseInt(e.target.value) : null,
                                    })
                                  }
                                  className="admin-form__select"
                                >
                                  <option value="">No Section</option>
                                  {sections.map((s) => (
                                    <option key={s.id} value={s.id}>
                                      {s.name}
                                    </option>
                                  ))}
                                </select>
                              </div>
                              {questionEditData.question_type === 'STAR_RATING' && (
                                <div className="admin-form-layout">
                                  <div className="admin-form__group">
                                    <label className="admin-form__label">Min Value</label>
                                    <input
                                      type="number"
                                      step="0.01"
                                      value={questionEditData.min_value || 1}
                                      onChange={(e) =>
                                        setQuestionEditData({
                                          ...questionEditData,
                                          min_value: parseFloat(e.target.value),
                                        })
                                      }
                                      className="admin-form__input"
                                    />
                                  </div>
                                  <div className="admin-form__group">
                                    <label className="admin-form__label">Max Value</label>
                                    <input
                                      type="number"
                                      step="0.01"
                                      value={questionEditData.max_value || 5}
                                      onChange={(e) =>
                                        setQuestionEditData({
                                          ...questionEditData,
                                          max_value: parseFloat(e.target.value),
                                        })
                                      }
                                      className="admin-form__input"
                                    />
                                  </div>
                                  <div className="admin-form__group">
                                    <label className="admin-form__label">Step Value</label>
                                    <input
                                      type="number"
                                      step="0.01"
                                      value={questionEditData.step_value || 0.25}
                                      onChange={(e) =>
                                        setQuestionEditData({
                                          ...questionEditData,
                                          step_value: parseFloat(e.target.value),
                                        })
                                      }
                                      className="admin-form__input"
                                    />
                                  </div>
                                </div>
                              )}
                              {questionEditData.question_type === 'CHOOSE_MULTIPLE' && (
                                <div className="admin-form__group">
                                  <label className="admin-form__label">Options (one per line)</label>
                                  <textarea
                                    value={
                                      questionEditData.options
                                        ?.map((opt) => opt.option_label)
                                        .join('\n') || ''
                                    }
                                    onChange={(e) => {
                                      const labels = e.target.value.split('\n').filter((l) => l.trim());
                                      setQuestionEditData({
                                        ...questionEditData,
                                        options: labels.map((label, idx) => {
                                          const trimmed = label.trim();
                                          return {
                                            option_value: trimmed.toLowerCase().replace(/\s+/g, '-'),
                                            option_label: trimmed,
                                            sort_order: idx,
                                          };
                                        }),
                                      });
                                    }}
                                    className="admin-form__textarea"
                                    rows={4}
                                    placeholder="Option 1&#10;Option 2&#10;Option 3"
                                  />
                                </div>
                              )}
                              <div className="admin-actions" style={{ marginTop: '0.75rem' }}>
                                <button
                                  onClick={() => handleSaveQuestion(q.id)}
                                  className="btn btn--primary btn--small"
                                >
                                  Save
                                </button>
                                <button
                                  onClick={handleCancelEditQuestion}
                                  className="btn btn--secondary btn--small"
                                >
                                  Cancel
                                </button>
                              </div>
                            </div>
                          ) : (
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                              <div style={{ flex: 1 }}>
                                <div style={{ fontWeight: 600, marginBottom: '0.25rem' }}>{q.question_text}</div>
                                <div style={{ fontSize: '0.875rem', opacity: 0.7 }}>
                                  {q.question_type}
                                  {q.question_type === 'STAR_RATING' &&
                                    ` (${q.min_value}-${q.max_value}, step: ${q.step_value})`}
                                  {q.question_type === 'CHOOSE_MULTIPLE' && q.options && (
                                    <span> - {q.options.length} options</span>
                                  )}
                                </div>
                              </div>
                              <div className="admin-actions">
                                <button
                                  onClick={() => handleEditQuestion(q)}
                                  className="btn btn--secondary btn--small"
                                >
                                  Edit
                                </button>
                                <button
                                  onClick={() => handleDeleteQuestion(q.id)}
                                  className="btn btn--danger btn--small"
                                >
                                  Delete
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
